import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiPlus } from 'react-icons/fi';
import {
  CalendarTimeline,
  type CalendarBar,
  type CalendarRow,
  type VariantStyle,
} from './CalendarTimeline';
import { buildCalendarAxis } from './calendarAxis';
import { useDatabase } from '../../hooks/useDatabase';
import { useResourceReservations } from '../../hooks/useResourceReservations';
import ResourceReservationForm from '../forms/ResourceReservationForm';

/**
 * Reservation timeline for resources managed by a given team. Shows one row
 * per resource with horizontal bars for each reservation in the window.
 *
 * Reuses `CalendarTimeline` so the period bands, weekend shading, and today
 * line match the team's Schedule panel.
 */

interface Props {
  teamId: string;
  /** ISO date — start of the displayed window. */
  startDate: string;
  /** ISO date — exclusive end of the displayed window. */
  endDate: string;
  /** Number of days per period in the calendar axis (matches team rhythm). */
  intervalDays?: number;
  /** Workspace id for resource detail links. */
  workspaceId: string | undefined;
}

type ReservationVariant = 'reserved' | 'blocked';

const VARIANT_STYLES: Record<ReservationVariant, VariantStyle> = {
  reserved: {
    idle: 'bg-cyan-500/60 text-white',
    hover: 'bg-cyan-500/80 text-white',
  },
  blocked: {
    idle: 'bg-gray-500/40 text-gray-200 border border-dashed border-gray-400/50',
    hover: 'bg-gray-500/60 text-gray-200 border border-dashed border-gray-400/50',
  },
};

function describeObject(object: any, taskTitlesById: Record<string, string>): string {
  if (!object) return 'Blocked';
  if (object.type === 'TaskId') {
    return taskTitlesById[object.id] ?? `Task ${object.id.slice(0, 8)}`;
  }
  if (object.type === 'Tag') return `#${object.tag}`;
  if (object.type === 'ProjectId') return `Project ${object.id.slice(0, 8)}`;
  return 'Reserved';
}

/** Return reservation's first and last day index within the window, or null if fully outside. */
function reservationBoundsInWindow(
  when: any,
  daysByIso: Record<string, number>,
  days: { date: string }[],
): { startIdx: number; endIdx: number } | null {
  if (!when) return null;
  const start = when.type === 'Day' ? when.day : when.start_inclusive;
  const end = when.type === 'Day' ? when.day : when.end_inclusive;
  if (!start || !end) return null;

  // Clamp to the window.
  const firstInWindow = days[0]?.date;
  const lastInWindow = days[days.length - 1]?.date;
  if (!firstInWindow || !lastInWindow) return null;
  if (end < firstInWindow || start > lastInWindow) return null;

  const clampedStart = start < firstInWindow ? firstInWindow : start;
  const clampedEnd = end > lastInWindow ? lastInWindow : end;

  const startIdx = daysByIso[clampedStart];
  const endIdx = daysByIso[clampedEnd];
  if (startIdx === undefined || endIdx === undefined) return null;
  return { startIdx, endIdx };
}

export const ResourceReservationPanel: React.FC<Props> = ({
  teamId,
  startDate,
  endDate,
  intervalDays,
  workspaceId,
}) => {
  const { database, applyCommand } = useDatabase();
  const { reservations } = useResourceReservations();
  const [showForm, setShowForm] = useState(false);
  const [defaultResourceId, setDefaultResourceId] = useState<string | undefined>(undefined);

  // Resources managed by this team (plus resources whose managed_by_team is
  // empty but that are members of a group managed by this team — deferred;
  // initial impl shows only direct ownership).
  const resources = useMemo(() => {
    if (!database) return [] as { id: string; name: string }[];
    const all = database.resources();
    const out: { id: string; name: string }[] = [];
    for (const [id, r] of Object.entries(all)) {
      const managedBy = (r as any).managed_by_team_id?.();
      if (managedBy === teamId) {
        out.push({ id, name: (r as any).name() });
      }
    }
    return out.sort((a, b) => a.name.localeCompare(b.name));
  }, [database, teamId]);

  const taskTitlesById = useMemo(() => {
    if (!database) return {} as Record<string, string>;
    const out: Record<string, string> = {};
    for (const [id, t] of Object.entries(database.tasks())) {
      out[id] = (t as any).title();
    }
    return out;
  }, [database]);

  const { days, periods } = useMemo(
    () => buildCalendarAxis({ startDate, endDate, intervalDays }),
    [startDate, endDate, intervalDays],
  );

  const daysByIso = useMemo(() => {
    const out: Record<string, number> = {};
    days.forEach((d, i) => {
      out[d.date] = i;
    });
    return out;
  }, [days]);

  const reservationsByResource = useMemo(() => {
    const out: Record<string, typeof reservations> = {};
    for (const r of reservations) {
      (out[r.resourceId] ||= []).push(r);
    }
    return out;
  }, [reservations]);

  const rows: CalendarRow[] = useMemo(() => {
    return resources.map((resource) => {
      const reservs = reservationsByResource[resource.id] ?? [];
      const bars: CalendarBar[] = [];
      for (const r of reservs) {
        const bounds = reservationBoundsInWindow(r.when, daysByIso, days);
        if (!bounds) continue;
        const variant: ReservationVariant = r.isBlock ? 'blocked' : 'reserved';
        bars.push({
          key: r.id,
          label: r.isBlock ? (r.remark ?? 'Blocked') : describeObject(r.object, taskTitlesById),
          startIdx: bounds.startIdx,
          endIdx: bounds.endIdx,
          variant,
          tooltipSuffix: r.remark && !r.isBlock ? `— ${r.remark}` : undefined,
        });
      }

      return {
        key: resource.id,
        labelContent: (
          <Link
            to={workspaceId ? `/workspaces/${workspaceId}/resources/${resource.id}` : '#'}
            className="text-xs font-medium truncate hover:text-cm-accent-blue transition-colors"
          >
            {resource.name}
          </Link>
        ),
        bars,
      };
    });
  }, [resources, reservationsByResource, daysByIso, days, taskTitlesById, workspaceId]);

  if (resources.length === 0) {
    return (
      <p className="text-sm text-cm-text-muted text-center py-6">
        No resources are managed by this team yet.
      </p>
    );
  }

  return (
    <>
      <div className="flex items-center justify-end mb-3">
        <button
          onClick={() => {
            setDefaultResourceId(undefined);
            setShowForm(true);
          }}
          className="flex items-center gap-1.5 px-2.5 py-1 rounded text-xs text-cm-text-muted hover:text-cm-text-primary border border-cm-border-subtle hover:border-cm-border-mid transition-colors"
        >
          <FiPlus className="w-3 h-3" /> New reservation
        </button>
      </div>

      <CalendarTimeline
        days={days}
        periods={periods}
        rows={rows}
        variantStyles={VARIANT_STYLES}
        emptyMessage="No resources managed by this team."
      />

      <ResourceReservationForm
        isOpen={showForm}
        onClose={() => setShowForm(false)}
        onSubmit={applyCommand}
        defaultResourceId={defaultResourceId}
      />
    </>
  );
};

export default ResourceReservationPanel;
